import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Loader, Panel, classnames } from "@culturehq/components";

import { Trans, formatThousand, t } from "app/utils";
import { useGet } from "app/hooks";
import { Params } from "@culturehq/client";
import { isNumber } from "lodash";
import Change from "./Change";

enum SummaryCardColor {
  TOTAL_REACTIONS = "TOTAL REACTIONS",
  TOTAL_COMMENTS = "TOTAL COMMENTS",
  TOTAL_IMPRESSIONS = "TOTAL EST. IMPRESSIONS"
}

type OnClickCardSummary = (selectedEntity: SummaryCardColor) => void;

type Summary = {
  entity: string;
  title: SummaryCardColor;
  total: number;
  change: number;
  showChange?: boolean;
  onClick: OnClickCardSummary;
}

type Totals = {
  totalComments: number;
  totalReactions: number;
  totalImpressions: number;
  changeComments: number;
  changeReactions: number;
  changeImpressions: number;
}

type DataRow = {
  label: string;
  reactions: number;
  comments: number;
  impressions: number;
}

type EngagementTrendResponse = {
  totals: Totals;
  data: Array<DataRow>;
}

type EngagementTrendSummaryProps = {
  entity: string;
  showChange?: boolean;
  totals: Totals;
  onClick: OnClickCardSummary;
}

const getColorClassname = (title: SummaryCardColor) => ({
  [SummaryCardColor.TOTAL_REACTIONS]: "TotalReactionsColor",
  [SummaryCardColor.TOTAL_COMMENTS]: "TotalCommentsColor",
  [SummaryCardColor.TOTAL_IMPRESSIONS]: "TotalImpressionsColor"
}[title]);

const getBackgroundColorClassname = (title: SummaryCardColor) => ({
  [SummaryCardColor.TOTAL_REACTIONS]: "BackgroundTotalReactionsColor",
  [SummaryCardColor.TOTAL_COMMENTS]: "BackgroundTotalCommentsColor",
  [SummaryCardColor.TOTAL_IMPRESSIONS]: "BackgroundTotalImpressionsColor"
}[title]);

const EngagementTrendSummaryCard = ({
  entity, title, total, change, showChange, onClick
}: Summary) => (
  <div
    onClick={() => onClick(title)}
    className={classnames(
      "SummaryCard",
      { [getColorClassname(title)]: true },
      { [getBackgroundColorClassname(title)]: entity === title }
    )}
  >
    <span className="Title">{title}</span>
    <div className="SummaryCardAmounts">
      <h2>{formatThousand(total)}</h2>
      {showChange && <Change change={change} />}
    </div>
  </div>
);

const EngagementTrendSummary = ({
  entity, showChange, totals, onClick
}: EngagementTrendSummaryProps) => (
  <div className="Columns">
    <EngagementTrendSummaryCard
      entity={entity}
      title={SummaryCardColor.TOTAL_REACTIONS}
      total={totals.totalReactions}
      change={totals.changeReactions}
      showChange={showChange}
      onClick={onClick}
    />
    <EngagementTrendSummaryCard
      entity={entity}
      title={SummaryCardColor.TOTAL_COMMENTS}
      total={totals.totalComments}
      change={totals.changeComments}
      showChange={showChange}
      onClick={onClick}
    />
    <EngagementTrendSummaryCard
      entity={entity}
      title={SummaryCardColor.TOTAL_IMPRESSIONS}
      total={totals.totalImpressions}
      change={totals.changeImpressions}
      showChange={showChange}
      onClick={onClick}
    />
  </div>
);

const EngagementTrend: React.FC<{params: Params}> = ({ params }) => {
  const get = useGet<EngagementTrendResponse>("/analytics/social/engagement_trends", params);
  const [entity, setEntity] = useState(SummaryCardColor.TOTAL_IMPRESSIONS);

  if (get.error) {
    throw new Error(t("Looks like there has been an unexpected error. Please reload the page."));
  }

  return (
    <Panel>
      <Panel.Body className="card_holder">
        <div className="card_title">
          <h3>
            <Trans>Engagement Trend</Trans>
          </h3>
        </div>
        <div className="StoryAnalyticsEngagement">
          <div className="Chart">
            {get.getting ? <Loader loading /> : (
              <ResponsiveContainer width="99%" height="100%">
                <LineChart
                  width={500}
                  height={300}
                  data={get.got.data}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis tickFormatter={formatThousand} />
                  <Tooltip
                    formatter={(value, _n, _p) => formatThousand(isNumber(value) ? value : 0)}
                    contentStyle={{
                      overflow: "hidden",
                      borderRadius: "6px",
                      backgroundColor: "#23314f",
                      color: "#fff",
                      textAlign: "center",
                      textTransform: "capitalize",
                      padding: "0 0 10px"
                    }}
                    labelStyle={{ backgroundColor: "#3b4860", padding: "5px 0", marginBottom: "5px" }}
                    itemStyle={{ margin: "0 10px" }}
                  />
                  <Legend formatter={value => `${value.charAt(0).toUpperCase()}${value.slice(1)}`} />
                  {entity === SummaryCardColor.TOTAL_REACTIONS && (
                    <Line type="monotone" dataKey="reactions" stroke="#8ab3d4" strokeWidth={3} />
                  )}

                  {entity === SummaryCardColor.TOTAL_COMMENTS && (
                    <Line type="monotone" dataKey="comments" stroke="#fed046" activeDot={{ r: 8 }} strokeWidth={3} />
                  )}

                  {entity === SummaryCardColor.TOTAL_IMPRESSIONS && (
                    <Line type="monotone" dataKey="impressions" stroke="#78b17c" strokeWidth={3} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {get.getting ? <Loader loading /> : (
            <EngagementTrendSummary
              entity={entity}
              showChange={!!params?.startDate}
              totals={get.got.totals}
              onClick={setEntity}
            />
          )}
        </div>
      </Panel.Body>
    </Panel>
  );
};


export default EngagementTrend;
